import { Box } from "@saleor/macaw-ui-next";
import { CSSProperties, RefObject, useLayoutEffect, useRef, useState } from "react";

import { LabelFieldContent } from "./LabelFieldContent";
import { LabelBox, LabelLayout, textFloorPt } from "./layout";
import { contentElement, resolveNameSkuBoxes } from "./resolveNameSkuBoxes";
import { LabelData } from "./types";
import { useMeasuredNameHeight } from "./useMeasuredNameHeight";

interface LabelPreviewCardProps {
  item: LabelData;
  layout: LabelLayout;
  widthMm: number;
  heightMm: number;
  // Design-space dimensions stay tied to the selected template while the
  // output label can change independently (e.g. a 50×150 template on 50×30
  // thermal stock). Font sizes are scaled against the design height too, so
  // the whole label — text included — scales like one image to whatever
  // size it prints at, and always keeps the proportions of the template
  // editor and gallery. (Scaling text against a different height than the
  // boxes are laid out at made it overflow or shrink inside them.)
  designWidthMm?: number;
  designHeightMm?: number;
  rotationDegrees?: 0 | 90 | 180 | 270;
  zoomPercent?: number;
  crop?: boolean;
  // Screen preview: scales to fill its grid/flex cell (aspect ratio only —
  // sizing is illustrative). Omit for the hidden print portal, where the
  // literal mm dimensions must be physically exact.
  fit?: boolean;
}

const PX_PER_MM = 96 / 25.4;

// How much a "fit" card has to shrink a label laid out at its real physical
// size to fill the card's width. Laying the preview out at the same mm size
// as the print copy (and only scaling the result) makes text wrap at
// exactly the same words — at a few pixels high, scaled-down text wraps
// differently than the printed label.
const useFitScale = (ref: RefObject<HTMLElement>, widthMm: number | null) => {
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const element = ref.current;

    if (!element || widthMm === null) {
      return;
    }

    const measure = () => {
      if (element.clientWidth) {
        setScale(element.clientWidth / (widthMm * PX_PER_MM));
      }
    };

    measure();

    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver(measure);

    observer.observe(element);

    return () => observer.disconnect();
  }, [ref, widthMm]);

  return widthMm === null ? 1 : scale;
};

const boxStyle = (box: LabelBox): CSSProperties => ({
  position: "absolute",
  left: `${box.x}%`,
  top: `${box.y}%`,
  width: `${box.width}%`,
  height: `${box.height}%`,
  overflow: "hidden",
});

export const LabelPreviewCard = ({
  item,
  layout,
  widthMm,
  heightMm,
  designWidthMm = widthMm,
  designHeightMm = heightMm,
  rotationDegrees = 0,
  zoomPercent = 100,
  crop = false,
  fit,
}: LabelPreviewCardProps) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const designRef = useRef<HTMLDivElement>(null);
  const fitScale = useFitScale(cardRef, fit ? widthMm : null);
  const measuredNameHeight = useMeasuredNameHeight(
    designRef,
    layout.elements.find(element => element.kind === "name")?.id,
  );
  const minFontSizePt = textFloorPt(layout, designHeightMm);
  const dynamicBoxes = resolveNameSkuBoxes(
    layout,
    item,
    designWidthMm,
    designHeightMm,
    measuredNameHeight,
  );
  const rotated = rotationDegrees % 180 !== 0;
  const rotatedWidth = rotated ? designHeightMm : designWidthMm;
  const rotatedHeight = rotated ? designWidthMm : designHeightMm;
  const labelScale = crop
    ? Math.max(widthMm / rotatedWidth, heightMm / rotatedHeight)
    : Math.min(widthMm / rotatedWidth, heightMm / rotatedHeight);
  const scale = labelScale * (zoomPercent / 100) * fitScale;

  return (
    <Box
      ref={cardRef}
      // The outline marks the label's edge on screen only: the print copy
      // (no `fit`) must not have it, or thermal printers print it as a
      // dotted frame around every label.
      borderWidth={fit ? 1 : 0}
      borderStyle="solid"
      borderColor="default1"
      borderRadius={fit ? 2 : undefined}
      style={{
        position: "relative",
        overflow: "hidden",
        boxSizing: "border-box",
        // Establishes a query container sized to the label's own physical
        // dimensions, so `cqh`-based font sizes (see LabelFieldContent) stay
        // proportional to the label at whatever pixel size it renders here —
        // shrunk-down grid thumbnail, or this same "fit" card at full size.
        containerType: "size",
        ...(fit
          ? { width: "100%", aspectRatio: `${widthMm} / ${heightMm}` }
          : { width: `${widthMm}mm`, height: `${heightMm}mm` }),
      }}
    >
      <Box
        ref={designRef}
        data-label-root
        style={{
          position: "absolute",
          left: "50%",
          top: "50%",
          // Always laid out at the real size, like the print copy; a "fit"
          // card shrinks it with fitScale instead (see useFitScale).
          width: `${designWidthMm}mm`,
          height: `${designHeightMm}mm`,
          transform: `translate(-50%, -50%) rotate(${rotationDegrees}deg) scale(${scale})`,
          transformOrigin: "center",
          boxSizing: "border-box",
          containerType: "size",
          overflow: "hidden",
        }}
      >
        {layout.elements.map(element => {
          const dynamicBox = dynamicBoxes.get(element.id);
          const renderElement = dynamicBox ? { ...element, box: dynamicBox } : element;

          return (
            <Box
              key={element.id}
              data-label-element={element.id}
              style={boxStyle(renderElement.box)}
            >
              <LabelFieldContent
                element={contentElement(element, renderElement)}
                item={item}
                heightMm={designHeightMm}
                widthMm={designWidthMm}
                minFontSizePt={minFontSizePt}
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
